import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useCustomerNavigate } from 'hooks/useCustomerNavigate';
import { useObjectIdRouteParam } from 'hooks/useObjectIdRouteParam';
import { usePermission } from 'hooks/usePermission';
import { useAppDispatch } from 'slices/store';
import { checkSavedDisabled } from 'utils/isSavedDisabled';
import { errorNotification } from 'utils/notifications';
import { EAuthStrategy } from '@timeedit/types/lib/enums';
import type { IAuthConfig } from '@timeedit/types/lib/types';
import { createAuthConfig as createTAuthConfig } from '@timeedit/types/lib/utils';
import {
  Button,
  ConfirmationPopup,
  DefaultContentLayout,
  Icon,
  Typography,
} from '@timeedit/ui-components';
import { Tabs } from '@timeedit/ui-components/antd';
import LoadingWrapper from '../../../components/LoadingWrapper';
import intl from '../../../i18n/intl';
import {
  authConfigsLoading,
  copyAuthConfig,
  createAuthConfig,
  deleteAuthConfig,
  deleteAuthConfigSuccess,
  saveAuthConfig,
} from '../../../slices/authConfig.slice';
import { fetchOrganizationHierarchy } from '../../../slices/organizationHierarchy.slice';
import { fetchRoles } from '../../../slices/roles.slice';
import { setBreadcrumbs } from '../../../slices/ui.slice';
import { authConfigApi, useGetAuthConfigsQuery } from './api/authConfig.api';
import AuthConfigDetailPage from './detail';
import './authConfig.scss';

const language: any = intl.messages;

const AuthConfigPage = () => {
  const dispatch = useAppDispatch();
  const navigate = useCustomerNavigate();
  const authConfigId = useObjectIdRouteParam();

  /**
   * SELECTORS
   */
  const { data: authConfigs = [], isLoading: isFetchingConfigs } =
    useGetAuthConfigsQuery();
  const originalAuthConfig = useMemo(
    () => authConfigs.find((c) => c.id === authConfigId),
    [authConfigs, authConfigId]
  );
  const [authConfig, setAuthConfig] = useState<IAuthConfig | undefined>(
    originalAuthConfig
  );
  const isMutatingConfigs = useSelector(authConfigsLoading);
  const isLoading = isFetchingConfigs || isMutatingConfigs;

  useEffect(() => {
    if (authConfigId !== authConfig?.id) setAuthConfig(originalAuthConfig);
  }, [authConfigId, originalAuthConfig]);

  const hasEditPermission = usePermission({
    permissions: ['TE_ADMIN::manageAuthConfigs'],
  });

  const hasTempAuthConfig = authConfigs.some((c) => c.id === 'new');

  const isNewConfig = authConfigId === 'new';
  const hasChanges =
    !!authConfig &&
    !!originalAuthConfig &&
    !checkSavedDisabled(authConfig, originalAuthConfig);

  /**
   * EFFECTS
   */
  useEffect(() => {
    // Jumping to newly saved new config
    if (
      authConfigId === 'new' &&
      authConfigs.length > 0 &&
      authConfigs.findIndex((el: IAuthConfig) => el.id === 'new') === -1
    )
      navigate(
        `/user-management/auth-configs/${
          authConfigs[authConfigs.length - 1].id
        }`,
        { replace: true }
      );
    // Default case
    if (!authConfigId && authConfigs && authConfigs[0] && authConfigs[0].id) {
      navigate(`/user-management/auth-configs/${authConfigs[0].id}`, {
        replace: true,
      });
    }
  }, [authConfigId, authConfigs, navigate]);

  useEffect(() => {
    dispatch(
      setBreadcrumbs([
        {
          path: `/user-management`,
          label: `${language.organizations_users}`,
        },
        {
          path: `/user-management/authentication`,
          label: `Auth configs`,
        },
      ])
    );
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchRoles());
    dispatch(fetchOrganizationHierarchy());
  }, []);

  /**
   * EVENT HANDLERS
   */
  const onSelectAuthConfig = (authConfigId: string | number) => {
    navigate(`/user-management/auth-configs/${authConfigId}`);
  };

  // Adds new authentication config
  const onAddAuthConfig = () => {
    dispatch(createAuthConfig());
    dispatch(
      authConfigApi.util.updateQueryData(
        'getAuthConfigs',
        undefined,
        (draft) => {
          if (!draft.find((c) => c.id === 'new')) {
            draft.push(
              createTAuthConfig({
                id: 'new',
                name: 'New auth config',
                strategy: EAuthStrategy.SAML2,
              })
            );
          }
        }
      )
    );
    if (authConfigId !== 'new') onSelectAuthConfig('new');
  };

  const onChange = (prop: string, value: any) => {
    setAuthConfig({ ...authConfig, [prop]: value } as IAuthConfig);
  };

  // Deletes chosen authentication config
  const onDeleteAuthConfig = (authConfigIdToDelete: string | number) => {
    const idToDelete = authConfigIdToDelete.toString();
    const config = authConfigs.filter(
      (el: IAuthConfig) => el.id === idToDelete
    );
    if (config.length && config[0].strategy === EAuthStrategy.TE_IDP) {
      const errorDetail = {
        message: 'An error happened',
        description: 'You cannot delete a TE_IDP authentication config',
      };
      errorNotification(errorDetail.message, [errorDetail.description]);
      return;
    }
    if (idToDelete === 'new') {
      // Local-only delete: the entity was never persisted, so skip the thunk,
      // and tag invalidation. Remove it from both stores directly.
      dispatch(deleteAuthConfigSuccess('new'));
      dispatch(
        authConfigApi.util.updateQueryData(
          'getAuthConfigs',
          undefined,
          (draft) => {
            const idx = draft.findIndex((c) => c.id === 'new');
            if (idx !== -1) draft.splice(idx, 1);
          }
        )
      );
    } else {
      dispatch(deleteAuthConfig(idToDelete));
    }
    if (authConfigId === idToDelete) navigate('/user-management/auth-configs');
  };

  // Copy authentication config
  const onCopy = (authConfigId: string | number) => {
    const config = authConfigs.find((c) => c.id === authConfigId.toString());
    if (!config) return;
    dispatch(copyAuthConfig(config));
  };

  const tabs = useMemo(
    () =>
      (authConfigs ?? [])
        .map((c) => ({
          key: c.id ?? '',
          label: c.name ?? '',
          closable: false,
        }))
        .filter((tab) => tab.key)
        .sort((a, b) => {
          // Put 'new' config last (to the right)
          if (a.key === 'new') return 1;
          else if (b.key === 'new') return -1;
          return 0; // keep rest of order the same
        }),
    [authConfigs]
  );

  return (
    <DefaultContentLayout
      title="Authentication Configs"
      subtitle="Manage authentication configs"
    >
      <Tabs
        className={`authConfig-tabs${
          hasTempAuthConfig ? ' authConfig-tabs--add-disabled' : ''
        }`}
        onChange={(key) => onSelectAuthConfig(key)}
        type="editable-card"
        activeKey={authConfigId}
        onEdit={() => !hasTempAuthConfig && onAddAuthConfig()}
        hideAdd={!hasEditPermission}
        addIcon={
          <span aria-hidden="true" className="authConfig-tabs__addButtonIcon">
            <Icon id="PlusOutlined" alt="" /> Add config
          </span>
        }
        locale={{ addAriaLabel: 'Add config' }}
        items={tabs}
      />
      <div style={{ display: 'flex', padding: '0 8px' }}>
        <div className="inner-content--wrapper allow-scroll">
          <LoadingWrapper isLoading={isLoading}>
            <div className="authConfig-header">
              <div className="authConfig-header__main">
                <Typography.Title
                  level={4}
                  className="authConfig-header__title"
                >
                  {authConfig?.name ?? ''}
                </Typography.Title>
                <div className="authConfig-actions">
                  <Button
                    size="middle"
                    disabled={
                      !hasEditPermission ||
                      !originalAuthConfig ||
                      isLoading ||
                      !hasChanges
                    }
                    onClick={() => setAuthConfig(originalAuthConfig)}
                  >
                    Discard changes
                  </Button>
                  <Button
                    size="middle"
                    type="primary"
                    disabled={
                      !hasEditPermission ||
                      !originalAuthConfig ||
                      isLoading ||
                      (!isNewConfig && !hasChanges)
                    }
                    onClick={() => {
                      if (!authConfig) return;
                      dispatch(saveAuthConfig(authConfig));
                    }}
                  >
                    Save
                  </Button>
                </div>
              </div>
              <div className="authConfig-secondary-actions">
                <Button
                  size="small"
                  icon={<Icon id="CopyOutlined" alt="" />}
                  disabled={
                    !hasEditPermission ||
                    !originalAuthConfig ||
                    !authConfigId ||
                    isLoading
                  }
                  onClick={() => authConfigId && onCopy(authConfigId)}
                >
                  Duplicate
                </Button>
                <ConfirmationPopup
                  key="delete"
                  icon={<Icon alt="Warning" id="Warning" />}
                  message="Are you sure you want to delete this auth configuration?"
                  onConfirm={() =>
                    authConfigId && onDeleteAuthConfig(authConfigId)
                  }
                  okText="Delete"
                  cancelText="Cancel"
                >
                  <Button
                    size="small"
                    icon={<Icon id="DeleteOutlined" alt="" />}
                    disabled={
                      !hasEditPermission ||
                      !originalAuthConfig ||
                      !authConfigId ||
                      isLoading
                    }
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                  >
                    Delete
                  </Button>
                </ConfirmationPopup>
              </div>
            </div>
            {authConfigId && authConfig && (
              <AuthConfigDetailPage
                onChange={onChange}
                authConfig={authConfig}
                originalAuthConfig={originalAuthConfig}
              />
            )}
          </LoadingWrapper>
        </div>
      </div>
    </DefaultContentLayout>
  );
};

export default AuthConfigPage;
